// client/src/coms/components/comslayout_parts/useActiveLocation.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPersistedLastLocation, setPersistedLastLocation } from './comsSession';
import { pickDefaultChannel } from './serverApi';
import { safePickServer } from './layoutUtils';

const LS_LAST_CHANNEL_BY_SERVER = 'coms:lastChannelByServer';
const LS_LAST_SERVER_ID = 'lastCommsServerId';

function readLastChannelMap() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    const raw = window.localStorage.getItem(LS_LAST_CHANNEL_BY_SERVER);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

function writeLastChannelMap(map) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(LS_LAST_CHANNEL_BY_SERVER, JSON.stringify(map && typeof map === 'object' ? map : {}));
  } catch {}
}

function readLastServerId() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return '';
    return String(window.localStorage.getItem(LS_LAST_SERVER_ID) || '').trim();
  } catch {
    return '';
  }
}

function writeLastServerId(serverId) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const sid = serverId != null ? String(serverId).trim() : '';
    if (!sid) return;
    window.localStorage.setItem(LS_LAST_SERVER_ID, sid);
  } catch {}
}

function getServerKey(server) {
  if (!server || typeof server !== 'object') return '';
  if (server.id != null && String(server.id).trim()) return String(server.id).trim();
  if (server.server_id != null && String(server.server_id).trim()) return String(server.server_id).trim();
  if (server.public_id != null && String(server.public_id).trim()) return String(server.public_id).trim();
  if (server.server_key != null && String(server.server_key).trim()) return String(server.server_key).trim();
  return '';
}

function findServerByKey(servers, key) {
  const wanted = String(key || '').trim();
  if (!wanted) return null;
  const list = Array.isArray(servers) ? servers : [];
  return list.find((s) => getServerKey(s) === wanted) || null;
}

function getRememberedChannelIdForServer(serverId) {
  const sid = serverId != null ? String(serverId) : '';
  if (!sid) return '';
  const map = readLastChannelMap();
  const v = map && typeof map === 'object' ? map[sid] : '';
  return String(v || '').trim();
}

function setRememberedChannelIdForServer(serverId, channelId) {
  const sid = serverId != null ? String(serverId) : '';
  if (!sid) return;
  const cid = channelId != null ? String(channelId) : '';
  const map = readLastChannelMap();
  const next = map && typeof map === 'object' ? { ...map } : {};
  if (cid) next[sid] = cid;
  else delete next[sid];
  writeLastChannelMap(next);
}

export default function useActiveLocation({ params, navigate, servers, serversLoaded }) {
  const [activeServerId, setActiveServerId] = useState(() => {
    const paramSid = params && params.serverId != null ? String(params.serverId).trim() : '';
    if (paramSid) return paramSid;

    const savedServerId = readLastServerId();
    if (savedServerId) return savedServerId;

    const last = getPersistedLastLocation();
    return last && last.serverId ? String(last.serverId).trim() : null;
  });

  const [activeChannelId, setActiveChannelId] = useState(() => {
    const pSid = params && params.serverId != null ? String(params.serverId).trim() : '';
    const initialSid = pSid || readLastServerId() || String((getPersistedLastLocation() && getPersistedLastLocation().serverId) || '').trim();
    if (!initialSid) return null;

    const perServer = getRememberedChannelIdForServer(initialSid);
    if (perServer) return perServer;

    const last = getPersistedLastLocation();
    if (!last) return null;
    return String(last.serverId || '').trim() === initialSid && last.channelId ? String(last.channelId).trim() : null;
  });

  const initialLocationRestoredRef = useRef(false);
  // Server we are navigating to. Router navigations are transitions, so for a render or two the URL still
  // names the previous server; without this the effects below would switch back to it and the server's
  // remembered channel would be lost (users landed on the first channel instead of where they left off).
  const pendingServerRef = useRef('');

  useEffect(() => {
    const paramSid = params && params.serverId != null ? String(params.serverId).trim() : '';

    if (paramSid) {
      if (pendingServerRef.current) {
        if (paramSid !== pendingServerRef.current) return;
        pendingServerRef.current = '';
      }
      if (paramSid !== String(activeServerId || '').trim()) {
        setActiveServerId(paramSid);
      }
      return;
    }

    if (!initialLocationRestoredRef.current) return;
    if (activeServerId == null) return;
  }, [params, activeServerId]);

  useEffect(() => {
    if (!serversLoaded) return;

    const paramSid = params && params.serverId != null ? String(params.serverId).trim() : '';

    if (paramSid) {
      const paramServer = findServerByKey(servers, paramSid);

      if (paramServer) {
        if (pendingServerRef.current && pendingServerRef.current !== paramSid) return;
        const resolvedSid = getServerKey(paramServer);
        if (resolvedSid && resolvedSid !== String(activeServerId || '').trim()) {
          setActiveServerId(resolvedSid);
        }
        return;
      }

      const savedSid = readLastServerId();
      const savedServer = findServerByKey(servers, savedSid);
      const fallbackServer = savedServer || (Array.isArray(servers) && servers.length ? servers[0] : null);
      const fallbackSid = getServerKey(fallbackServer);

      if (fallbackSid) {
        setActiveServerId(fallbackSid);
        navigate(`/comms/${fallbackSid}`, { replace: true });
        return;
      }

      setActiveServerId(null);
      navigate('/comms', { replace: true });
      return;
    }

    const currentActive = findServerByKey(servers, activeServerId);
    if (currentActive) return;

    const savedSid = readLastServerId();
    const savedServer = findServerByKey(servers, savedSid);

    const last = getPersistedLastLocation();
    const persistedSid = last && last.serverId ? String(last.serverId).trim() : '';
    const persistedServer = findServerByKey(servers, persistedSid);

    const fallbackServer =
      savedServer ||
      persistedServer ||
      (Array.isArray(servers) && servers.length ? servers[0] : null);

    const fallbackSid = getServerKey(fallbackServer);

    if (fallbackSid) {
      setActiveServerId(fallbackSid);
      navigate(`/comms/${fallbackSid}`, { replace: true });
      return;
    }

    setActiveServerId(null);
    navigate('/comms', { replace: true });
  }, [serversLoaded, servers, params, activeServerId, navigate]);

  const server = useMemo(() => {
    const picked = safePickServer(servers, activeServerId);
    if (picked) return picked;
    return findServerByKey(servers, activeServerId);
  }, [servers, activeServerId]);

  useEffect(() => {
    if (!server) {
      setActiveChannelId(null);
      return;
    }

    const sid = getServerKey(server);
    const channels = Array.isArray(server.channels) ? server.channels : [];

    const perServerRemembered = getRememberedChannelIdForServer(sid);

    const last = getPersistedLastLocation();
    const globalRemembered =
      last && String(last.serverId || '').trim() === sid ? String(last.channelId || '').trim() : '';

    const persistedChannelId = perServerRemembered || globalRemembered || '';

    const hasPersisted = persistedChannelId && channels.some((c) => String(c.id) === persistedChannelId);
    const hasCurrent = activeChannelId != null && channels.some((c) => String(c.id) === String(activeChannelId));

    if (hasCurrent) return;

    if (hasPersisted) {
      setActiveChannelId(persistedChannelId);
      return;
    }

    const def = pickDefaultChannel(server);
    setActiveChannelId(def);
  }, [server, activeChannelId]);

  const activeChannel = useMemo(() => {
    if (!server) return null;
    const channels = Array.isArray(server.channels) ? server.channels : [];
    return channels.find((c) => String(c.id) === String(activeChannelId)) || null;
  }, [server, activeChannelId]);

  const activeChannelReady = useMemo(() => {
    if (!server) return false;
    const channels = Array.isArray(server.channels) ? server.channels : [];
    if (!channels.length) return true;
    if (activeChannelId == null || String(activeChannelId) === '') return false;
    return channels.some((c) => String(c.id) === String(activeChannelId));
  }, [server, activeChannelId]);

  useEffect(() => {
    const sid = server ? getServerKey(server) : activeServerId != null ? String(activeServerId).trim() : '';
    const cid = activeChannelId != null ? String(activeChannelId).trim() : '';

    if (!sid) return;

    writeLastServerId(sid);

    if (!cid) return;

    setRememberedChannelIdForServer(sid, cid);
    setPersistedLastLocation(sid, cid);
  }, [server, activeServerId, activeChannelId]);

  useEffect(() => {
    if (initialLocationRestoredRef.current) return;
    if (!serversLoaded) return;

    const list = Array.isArray(servers) ? servers : [];
    if (!list.length) {
      initialLocationRestoredRef.current = true;
      return;
    }

    const paramSid = params && params.serverId != null ? String(params.serverId).trim() : '';
    if (paramSid) {
      const paramServer = findServerByKey(list, paramSid);
      if (paramServer) {
        const resolvedSid = getServerKey(paramServer);
        if (resolvedSid) {
          setActiveServerId(resolvedSid);
          writeLastServerId(resolvedSid);
        }
      }
      initialLocationRestoredRef.current = true;
      return;
    }

    const savedSid = readLastServerId();
    const savedServer = findServerByKey(list, savedSid);

    const last = getPersistedLastLocation();
    const persistedSid = last && last.serverId ? String(last.serverId).trim() : '';
    const persistedServer = findServerByKey(list, persistedSid);

    const targetServer = savedServer || persistedServer || list[0] || null;
    if (!targetServer) {
      initialLocationRestoredRef.current = true;
      return;
    }

    const sid = getServerKey(targetServer);
    const channels = Array.isArray(targetServer.channels) ? targetServer.channels : [];

    const perServerRemembered = getRememberedChannelIdForServer(sid);
    const savedCidRaw =
      perServerRemembered ||
      (last && String(last.serverId || '').trim() === sid ? String(last.channelId || '').trim() : '');

    const savedCid = String(savedCidRaw || '').trim();
    const resolvedCid = savedCid && channels.some((c) => String(c.id) === savedCid) ? savedCid : '';

    setActiveServerId(sid);
    writeLastServerId(sid);

    if (resolvedCid) {
      setActiveChannelId(resolvedCid);
      setPersistedLastLocation(sid, resolvedCid);
    }

    navigate(`/comms/${sid}`, { replace: true });
    initialLocationRestoredRef.current = true;
  }, [serversLoaded, servers, params, navigate]);

  const onSelectServer = useCallback(
    (serverId, { mobile, closePanelsRef } = {}) => {
      const sid = serverId != null ? String(serverId).trim() : '';
      if (!sid) return;

      const paramSid = params && params.serverId != null ? String(params.serverId).trim() : '';
      if (sid !== paramSid) pendingServerRef.current = sid;
      setActiveServerId(sid);
      writeLastServerId(sid);

      try {
        const rememberedCid = getRememberedChannelIdForServer(sid);
        if (rememberedCid) {
          setActiveChannelId(rememberedCid);
          setPersistedLastLocation(sid, rememberedCid);
        } else {
          setPersistedLastLocation(sid, '');
        }
      } catch {}

      if (mobile && closePanelsRef && closePanelsRef.current) closePanelsRef.current();
      navigate(`/comms/${sid}`);
    },
    [navigate, params]
  );

  const onSelectChannel = useCallback(
    (channelId) => {
      const cid = channelId != null ? String(channelId) : null;
      setActiveChannelId(cid);

      const sid = activeServerId != null ? String(activeServerId).trim() : '';
      if (!sid) return;

      writeLastServerId(sid);
      setRememberedChannelIdForServer(sid, cid || '');
      setPersistedLastLocation(sid, cid || '');
    },
    [activeServerId]
  );

  // Jumps straight to a channel on any server (conversation tabs), in one step so the server's remembered
  // channel never flashes first.
  const goTo = useCallback(
    (serverId, channelId) => {
      const sid = serverId != null ? String(serverId).trim() : '';
      const cid = channelId != null ? String(channelId).trim() : '';
      if (!sid) return;
      const sameServer = sid === String(activeServerId || '').trim();
      const paramSid = params && params.serverId != null ? String(params.serverId).trim() : '';
      if (sid !== paramSid) pendingServerRef.current = sid;
      setActiveServerId(sid);
      writeLastServerId(sid);
      if (cid) {
        setActiveChannelId(cid);
        setRememberedChannelIdForServer(sid, cid);
        setPersistedLastLocation(sid, cid);
      }
      if (!sameServer || sid !== paramSid) navigate(`/comms/${sid}`);
    },
    [activeServerId, navigate, params]
  );

  return {
    server,
    activeServerId,
    setActiveServerId,
    activeChannelId,
    setActiveChannelId,
    activeChannel,
    activeChannelReady,
    onSelectServer,
    onSelectChannel,
    goTo,
  };
}